import React, { useState } from "react";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import DataGrid, {
  Column,
  Editing,
  Button,
  Texts,
  Lookup,
  Form,
  Item,
  SearchPanel,
  Scrolling,
  RequiredRule,
  Popup,
  Toolbar,
} from "devextreme-react/data-grid";
import { Popup as MyPopup } from "devextreme-react/popup";
import { Button as MyButton } from "devextreme-react/button";
import ScrollView from "devextreme-react/scroll-view";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinnerModal from "../../Components/LoadingSpinnerModal";
import { apiBaseURL } from "../../Utils/ProjectConstants";
import useFetch from "../../Hooks/useFetch";
import useFetchSupervisors from "../../Hooks/dataFetcher/useFetchSupervisors";
import ActionNotifications from "../../Components/ActionNotifications";
import ErrorMessage from "../../Components/ErrorMessage";
import { checkRoles } from "../../Utils/helper";

import "./styles.css";
library.add(fas);

const projectTypeOptions = [
  { label: "Federal", value: "Federal" },
  { label: "Industry", value: "Industry" },
  { label: "Overhead", value: "Overhead" },
  { label: "Work-Center-Overhead", value: "Work-Center-Overhead" },
];

export default function ProjectsPage() {
  const navigate = useNavigate(),
    fetch = useFetch(navigate),
    [notifications, setNotifications] = useState([]),
    [showSpinner, setShowSpinner] = useState(false),
    [newProject, setNewProject] = useState(false),
    [showDeactiveProjects, setShowDeactiveProjects] = useState(false),
    [state, setState] = useState({
      showInfoModal: false,
      deleteId: "",
      deleteName: "",
    }),
    { data: companyData, error: companyError } = useSWR(
      `${apiBaseURL}/api/Company`
    ),
    allCompanies = companyData?.map((x) => ({ label: x.name, value: x.id })),
    { data: additionalBillingData, error: additionalBillingError } = useSWR(
      `${apiBaseURL}/api/billingTypes/additional`
    ),
    additionalBillingTypes = additionalBillingData?.data,
    { data, mutate, error } = useSWR(
      `${apiBaseURL}/api/project?showDeleted=${showDeactiveProjects}`
    ),
    projects = data?.data?.map((x) => ({
      id: x.id,
      name: x.name,
      type: x.type,
      budgetYear: x.budgetYear,
      additionalBillingTypeId: x.additionalBillingTypeId,
      companyId: x.company ? x.company.id : null,
      managers: x.managers.map((x) => x.id),
      deleted: x.isDeleted,
    }));

  const { allSupervisors, error: supervisorError } = useFetchSupervisors();

  const fetchSuccessful = (response) => {
    let fetchNotifications = null;
    let success = false;

    if (
      response?.success &&
      response?.data?.statusCode &&
      response?.success !== "Invalid Model"
    ) {
      let message = response.data.data;

      //console.log('response', response)
      success = true;
      fetchNotifications = {
        isMessageInGDStanding: true,
        message: message,
      };
    } else if (Array.isArray(response?.data?.messeges)) {
      fetchNotifications = {
        isMessageInGDStanding: false,
        message: String(response.data.messeges),
      };
    } else if (response?.errors) {
      fetchNotifications = {
        isMessageInGDStanding: false,
        message: String(response.errors),
      };
    } else {
      fetchNotifications = [
        {
          isMessageInGDStanding: false,
          message:
            "An error has occurred. Please try again, and if the problem persists, contact the ATLAS software team.",
        },
      ];
    }
    //console.log('success', success)
    setNotifications((prevNotifications) => [
      ...prevNotifications,
      fetchNotifications,
    ]);
    return success;
  };

  const onShowModal = (e) => {
    //get rid of this?
    if (state.showInfoModal) {
      const proj = data.data.find(
        (x) => x.id === state.deleteId
      ); /*array within an array - not correct*/

      return (
        <>
          <MyPopup
            visible={state.showInfoModal}
            onHiding={() =>
              setState({
                ...state,
                showInfoModal: false,
                deleteId: "",
                deleteName: "",
              })
            }
            hideOnOutsideClick={true}
            title="Edit History"
            width="75%"
            //height={500}
            height="auto"
            resizeEnabled={true}
          >
            <ScrollView height="100%" width="100%">
              <ul>
                {proj.comments.length > 0 ? (
                  proj.comments
                    .map((x) => <li key={x.id}>{x.comments}</li>)
                    .reverse()
                ) : (
                  <div>No Edits</div>
                )}
              </ul>
            </ScrollView>
          </MyPopup>
        </>
      );
    }
  };

  const onRowRemoved = async (e) => {
    setShowSpinner(true);
    console.log("delete e", e);
    const response = await fetch.delete(
      `${apiBaseURL}/api/project?id=${e?.data?.id}`
    );

    if (fetchSuccessful(response)) {
      mutate();
    }
    setShowSpinner(false);
    setState({
      ...state,
      deleteId: "",
      deleteName: "",
    });
  };

  const renderGrid = () => {
    const onRowPrepared = (e) => {
      if (e.data) {
        if (e.data.deleted) {
          //e.rowElement.style.backgroundColor = '#ffcdcd';
          e.rowElement.style.color = "#f63e4b";
        }
      }
      if (e.rowType === "header") {
        e.rowElement.style.backgroundColor = "#FFCD00";
        e.rowElement.style.fontWeight = "bold";
      }
    };

    const onInitNewRow = (e) => {
      e.data.overideUsername = false;
      setNewProject(true);
    };
    const onEditingStart = (e) => {
      setNewProject(false);
    };

    const isButtonVisible = (e) => {
      if (e.row.isEditing) {
        return !e.row.isEditing;
      } else {
        return !e.row.data.deleted;
      }
    };
    const isButtonInvisible = (e) => {
      return !e.row.isEditing;
    };

    const onContentReady = (e) => {
      e.component.filter("deleted", "=", showDeactiveProjects);
    };

    const cellTemplate = (container, options) => {
      const noBreakSpace = "\u00A0";
      const text = (options.value || [])
        .map((element) => options.column.lookup.calculateCellValue(element))
        .join(", ");
      container.textContent = text || noBreakSpace;
      container.title = text;
    };

    const onEditorPreparing = (e) => {
      if (
        (e.parentType === "dataRow" || e.parentType === "filterRow") &&
        e.dataField === "managers"
      ) {
        e.editorName = "dxTagBox";
        e.editorOptions.dataSource = allSupervisors;
        e.editorOptions.showSelectionControls = true;
        e.editorOptions.displayExpr = "label";
        e.editorOptions.valueExpr = "value";
        e.editorOptions.value = e.value || [];
        //e.editorOptions.customItemCreateEvent;
        e.editorOptions.onValueChanged = (x) => {
          //replace?
          console.log(x);
          e.setValue(x.value);
        };
      }
    };

    const allowEditing = async (e) => {
      setShowSpinner(true);
      let response = null;
      let editType = e.changes.find((x) => x.type === "update");
      let addType = e.changes.find((x) => x.type === "insert");

      if (editType) {
        const editBody = {
          name: editType.data.name,
          managerIds: editType.data.managers,
          companyId: editType.data.companyId,
          typeId: editType.data.type.type,
          budgetYear: editType.data.budgetYear,
          additionalBillingTypeId: editType.data.additionalBillingTypeId,
          remarks: editType.data.remarks,
          id: editType.data.id,
        };
        console.log("editBody", editBody);
        response = await fetch.put(`${apiBaseURL}/api/project`, editBody);
      } else if (addType) {
        const addBody = {
          name: addType.data.name,
          managerIds: addType.data.managers,
          companyId: addType.data.companyId,
          typeId: addType.data.type.type,
          budgetYear: addType.data.budgetYear,
          additionalBillingTypeId: addType.data.additionalBillingTypeId,
        };
        console.log("addBody", addBody);
        response = await fetch.post(`${apiBaseURL}/api/project`, addBody);
      }

      if (fetchSuccessful(response)) {
        mutate();
      }
      setShowSpinner(false);
    };

    return (
      <>
        <DataGrid
          dataSource={projects}
          allowColumnReordering={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
          repaintChangesOnly={true}
          showBorders={true}
          height="75vh"
          onContentReady={onContentReady}
          onEditorPreparing={onEditorPreparing}
          onSaved={allowEditing}
          onRowPrepared={onRowPrepared}
          onRowRemoved={onRowRemoved}
          onInitNewRow={onInitNewRow}
          onEditingStart={onEditingStart}
        >
          <Scrolling mode="virtual" />
          <Toolbar>
            <Item location="before">
              <h1 className="white">Projects</h1>
            </Item>
            <Item name="addRowButton" />
            <Item name="searchPanel" />
          </Toolbar>
          {checkRoles(4) ? (
            <Editing
              allowAdding={checkRoles(4)}
              allowUpdating={checkRoles(4)}
              allowDeleting={checkRoles(4)}
              mode="popup"
            >
              <Popup
                title={newProject ? "Add Project" : "Edit Project"}
                showTitle={true}
                maxWidth={700}
                height="auto"
              />
              <Form>
                <Item itemType="group" colCount={2} colSpan={2}>
                  <Item dataField="name" colSpan={2} isRequired={true} />
                  <Item dataField="type.type" colSpan={2} isRequired={true} />
                  <Item dataField="budgetYear" colSpan={2} isRequired={true} />
                  <Item
                    dataField="additionalBillingTypeId"
                    colSpan={2}
                    isRequired={true}
                  />
                  <Item dataField="companyId" colSpan={2} isRequired={true} />
                  <Item dataField="managers" colSpan={2} isRequired={true} />
                  <Item
                    dataField="remarks"
                    colSpan={2}
                    visible={!newProject}
                    isRequired={!newProject}
                  />
                </Item>
              </Form>
              <Texts confirmDeleteMessage="Are you sure you want to delete this project? All tasks associated with this project will be deleted as well." />
            </Editing>
          ) : null}
          <SearchPanel
            id="searchpanel"
            visible={true}
            maxWidth={275}
            searchVisibleColumnsOnly={true}
            placeholder="Search..."
          />

          <Column type="buttons">
            {" "}
            {/*FIX NAME*/}
            <Button
              text="Work Plan"
              icon="info"
              visible={isButtonInvisible}
              onClick={(params) => navigate(`/projects/${params.row.data.id}`)}
            />
            <Button
              text="Edit History"
              icon="clock"
              visible={isButtonInvisible}
              onClick={(params) =>
                setState({
                  ...state,
                  deleteId: params.row.data.id,
                  deleteName: params.row.data.name,
                })
              }
            />
            <Button name="edit" visible={checkRoles(4) && isButtonVisible} />
            <Button name="delete" visible={checkRoles(4) && isButtonVisible} />
          </Column>
          <Column
            dataField="id"
            width={100}
            alignment="left"
            allowEditing={false}
          />
          <Column dataField="name" minWidth={200}>
            {" "}
            <RequiredRule />{" "}
          </Column>
          <Column dataField="type.type" caption="Type">
            <Lookup
              dataSource={projectTypeOptions}
              valueExpr="value"
              displayExpr="label"
            />
            <RequiredRule />
          </Column>
          <Column dataField="budgetYear">
            <RequiredRule />
          </Column>
          <Column
            dataField="additionalBillingTypeId"
            caption="Additional Billing Type"
          >
            <Lookup
              dataSource={additionalBillingTypes}
              valueExpr="id"
              displayExpr="name"
            />
            <RequiredRule />
          </Column>
          <Column dataField="companyId" caption="Company">
            <Lookup
              dataSource={allCompanies}
              valueExpr="value"
              displayExpr="label"
            />
            <RequiredRule />
          </Column>
          <Column
            dataField="managers"
            caption="Managers"
            cellTemplate={cellTemplate}
          >
            <Lookup
              dataSource={allSupervisors}
              valueExpr="value"
              displayExpr="label"
            />
            <RequiredRule />
          </Column>
          <Column dataField="remarks" visible={false} />
        </DataGrid>
      </>
    );
  };

  if (!data && !error) {
    return <LoadingSpinnerModal show />;
  } else {
    return (
      <div className="px-lg-4 page-content">
        {onShowModal()}
        <ActionNotifications arrayOfToasts={notifications} />
        <LoadingSpinnerModal show={showSpinner} />
        <div className="px-2">
          {/*<div className="row my-4">*/}
          {/*    <h1 className="black">All Projects</h1>*/}
          {/*    <h5 className="gray">{checkRoles(5) ? 'All projects that project managers have created.' : 'Projects that have been assigned to you.'}</h5>*/}
          {/*</div>*/}
          <div className="contentBox my-3">
            {!error && !supervisorError ? (
              <>
                {renderGrid()}
                <br />
                {data.data.length > 0 ? (
                  <>
                    <MyButton
                      className="myBtn"
                      visible={checkRoles(5) ? true : false}
                      text={
                        showDeactiveProjects
                          ? "Active Projects"
                          : "Deactivated Projects"
                      }
                      type="default"
                      stylingMode="contained"
                      onClick={() =>
                        setShowDeactiveProjects(!showDeactiveProjects)
                      }
                    />
                  </>
                ) : null}
              </>
            ) : (
              <ErrorMessage />
            )}
          </div>
        </div>
      </div>
    );
  }
}
